import * as React from 'react';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { Maximize2, Minimize2 } from 'lucide-react';
import styled from 'styled-components';

import { Colors } from 'services/ColorService';

import { useFullscreen } from './useFullscreen';

/**
 * Privacy requirement (spec 0010 §5): the excalidraw runtime loads its
 * fonts/vendor chunk from `EXCALIDRAW_ASSET_PATH`. Point it at the
 * generated copies under `public/excalidraw-assets{,-dev}/` — set at
 * MODULE SCOPE so it is defined before the lazy chunk below resolves.
 * Result: zero external/CDN requests with a board open.
 */
(window as Window & {
  EXCALIDRAW_ASSET_PATH?: string;
}).EXCALIDRAW_ASSET_PATH = `${process.env.PUBLIC_URL || ''}/`;

/**
 * `@excalidraw/excalidraw` is pinned to exactly 0.17.6 (last CJS line —
 * webpack 4 cannot bundle the ESM-only 0.18.x; same pin as tmslms_client
 * so scene formats match). Lazy-loaded so the ~1 MB editor bundle stays
 * out of the main chunk and only loads on the whiteboard page.
 */
const Excalidraw = lazy(() =>
  import('@excalidraw/excalidraw').then((m) => ({ default: m.Excalidraw })),
);

type IExcalidrawInitialData = React.ComponentProps<
  typeof Excalidraw
>['initialData'];

/**
 * Both derived from the component's own props rather than deep-imported from
 * `@excalidraw/excalidraw/types/types` — keeps the editor's types out of this
 * module's import graph (the runtime chunk stays lazy) and cannot drift.
 */
type IExcalidrawApi = Parameters<
  NonNullable<React.ComponentProps<typeof Excalidraw>['excalidrawAPI']>
>[0];
type IBinaryFileData = Parameters<IExcalidrawApi['addFiles']>[0][number];

/** One S3-backed board image — `WhiteboardType.files` (spec 0010 rev.3). */
export interface IWhiteboardFile {
  fileId: string;
  mimeType?: string | null;
  url: string;
}

interface IExcalidrawViewerProps {
  /** Raw `serializeAsJSON` payload from `WhiteboardType.sceneJson`. */
  sceneJson: string;
  /**
   * Board images, each `url` a short-TTL presigned S3 GET link. Scenes saved
   * since rev.3 keep only `fileId` refs on their image elements, so without
   * these the canvas draws blank placeholders. Older scenes still carry their
   * images inline (`parseScene` → `initialData.files`) and need nothing here.
   */
  files?: IWhiteboardFile[];
}

/** Stable identity — a fresh `[]` default would re-run the hydrate effect every render. */
const NO_FILES: IWhiteboardFile[] = [];

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** JSON.parse guard — a corrupt scene renders an empty board, not a crash. */
const parseScene = (sceneJson: string): IExcalidrawInitialData => {
  try {
    const parsed = JSON.parse(sceneJson);
    const appState = { ...(parsed?.appState ?? {}) };
    // Serialized appState may carry a plain-object `collaborators`;
    // excalidraw expects a Map there — drop it.
    delete appState.collaborators;
    return {
      elements: parsed?.elements ?? [],
      appState,
      files: parsed?.files ?? undefined,
      scrollToContent: true,
    };
  } catch {
    return { elements: [], appState: {} };
  }
};

/**
 * Read-only Excalidraw canvas (spec 0010). `viewModeEnabled` is
 * hardcoded — the ERP never edits a board, staff only view what the
 * teacher drew in the LMS.
 */
export const ExcalidrawViewer = memo(
  ({ sceneJson, files = NO_FILES }: IExcalidrawViewerProps) => {
    const { t } = useTranslation();
    const initialData = useMemo(() => parseScene(sceneJson), [sceneJson]);
    const containerRef = useRef<HTMLDivElement>(null);
    const { isFullscreen, toggle, isSupported } = useFullscreen(containerRef);

    // Excalidraw hands the imperative API back through a callback prop (0.17
    // has no ref forwarding). Park it in a ref and flip a flag — the flag is
    // what re-runs the hydrate effect, the ref is what it reads.
    const apiRef = useRef<IExcalidrawApi | null>(null);
    const [apiReady, setApiReady] = useState(false);
    /** fileIds already fetched (or in flight) — never download one twice. */
    const hydratedRef = useRef<Set<string>>(new Set());

    const handleExcalidrawApi = useCallback((api: IExcalidrawApi) => {
      apiRef.current = api;
      setApiReady(true);
    }, []);

    useEffect(() => {
      const api = apiRef.current;
      if (!apiReady || !api || !files.length) return undefined;

      const pending = files.filter((f) => !hydratedRef.current.has(f.fileId));
      if (!pending.length) return undefined;

      // Claim the ids before awaiting: a `cache-and-network` refetch hands us a
      // new `files` array while these fetches are still in flight, and an
      // unclaimed second pass would re-download every image.
      pending.forEach((f) => hydratedRef.current.add(f.fileId));

      let cancelled = false;

      (async () => {
        const loaded = await Promise.all(
          pending.map(
            async (f): Promise<IBinaryFileData | null> => {
              try {
                const response = await fetch(f.url);
                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}`);
                }
                const blob = await response.blob();
                const dataURL = await blobToDataUrl(blob);
                return {
                  id: f.fileId as IBinaryFileData['id'],
                  dataURL: dataURL as IBinaryFileData['dataURL'],
                  // The presigned response's own Content-Type beats guessing when
                  // the board row never recorded one.
                  mimeType: (f.mimeType ||
                    blob.type ||
                    'image/png') as IBinaryFileData['mimeType'],
                  created: Date.now(),
                };
              } catch (e) {
                // An expired link or a 403 costs one blank placeholder — never
                // the whole board.
                // eslint-disable-next-line no-console
                console.warn(
                  `[whiteboard] image ${f.fileId} failed to load`,
                  e,
                );
                // Let a later refetch (with a fresh presigned url) try again.
                hydratedRef.current.delete(f.fileId);
                return null;
              }
            },
          ),
        );

        if (cancelled) return;
        const ready = loaded.filter((f): f is IBinaryFileData => f !== null);
        if (ready.length) api.addFiles(ready);
      })();

      return () => {
        cancelled = true;
      };
    }, [apiReady, files]);

    const fullscreenLabel = isFullscreen
      ? t('pages.lessonWhiteboard.fullscreen.exit')
      : t('pages.lessonWhiteboard.fullscreen.enter');

    // Excalidraw renders this slot in view mode too (only the Library button
    // next to it is gated by `viewModeEnabled`) and lays it out alongside its
    // own controls — no overlap with the help icon, no z-index war. It also
    // lives inside the element we send fullscreen, so it stays reachable there.
    const renderTopRightUI = useCallback(
      () =>
        isSupported ? (
          <FullscreenButton
            type="button"
            onClick={toggle}
            title={fullscreenLabel}
            aria-label={fullscreenLabel}
          >
            {isFullscreen ? (
              <Minimize2 size={18} strokeWidth={2} />
            ) : (
              <Maximize2 size={18} strokeWidth={2} />
            )}
          </FullscreenButton>
        ) : null,
      [fullscreenLabel, isFullscreen, isSupported, toggle],
    );

    return (
      <CanvasContainer ref={containerRef}>
        <Suspense
          fallback={
            <LoaderWrapper>
              <Spin size="large" />
            </LoaderWrapper>
          }
        >
          <Excalidraw
            initialData={initialData}
            viewModeEnabled
            renderTopRightUI={renderTopRightUI}
            excalidrawAPI={handleExcalidrawApi}
          />
        </Suspense>
      </CanvasContainer>
    );
  },
);

const CanvasContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 260px);
  min-height: 600px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  overflow: hidden;
  background: ${Colors.White};

  /* In fullscreen the container IS the viewport — drop the framing so the
     canvas fills the screen (the height calc above would leave a gap). */
  &:fullscreen,
  &:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    min-height: 100vh;
    border: none;
    border-radius: 0;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/* Sized/coloured off excalidraw's own CSS vars so the button reads as part of
   its toolbar rather than an app control dropped on the canvas. */
const FullscreenButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--default-button-size, 2.25rem);
  height: var(--default-button-size, 2.25rem);
  padding: 0;
  cursor: pointer;
  color: var(--icon-fill-color, ${Colors.Black});
  background: var(--island-bg-color, ${Colors.White});
  border: 1px solid var(--default-border-color, ${Colors.SecondLightGray});
  border-radius: var(--border-radius-lg, 0.5rem);
  box-shadow: var(--shadow-island, 0 2px 6px ${Colors.BlueShadow});

  &:hover {
    background: var(--button-hover-bg, ${Colors.BasicBlueLight});
  }
`;
